import type { Direction } from "@attendik/types/schemas";

interface DirectionToggleProps {
  direction: Direction;
  onChange: (direction: Direction) => void;
  // Live cumulative counts shown on each button. Undefined until first load.
  enteredCount?: number;
  exitedCount?: number;
}

// Two large side-by-side buttons. The active direction is filled with its
// colour (green ENTER / red EXIT); the inactive one is muted/outlined. Each
// button also shows the running count for its direction. Tapping switches
// immediately — no confirmation.
export function DirectionToggle({
  direction,
  onChange,
  enteredCount,
  exitedCount
}: DirectionToggleProps): JSX.Element {
  const baseClasses =
    "flex h-full flex-1 flex-col items-center justify-center gap-0.5 transition-colors focus:outline-none focus:ring-4 focus:ring-inset";
  const labelClasses = "text-2xl font-bold uppercase tracking-wide";
  const countClasses = "text-sm font-semibold tabular-nums opacity-90";

  const enterActive = direction === "ENTER";
  const exitActive = direction === "EXIT";

  return (
    <div
      className="flex h-20 w-full select-none"
      role="group"
      aria-label="Scan direction"
    >
      <button
        type="button"
        aria-pressed={enterActive}
        onClick={() => onChange("ENTER")}
        className={`${baseClasses} ${
          enterActive
            ? "bg-green-600 text-white focus:ring-green-300"
            : "bg-slate-800 text-green-400 border-2 border-green-700 focus:ring-green-700"
        }`}
      >
        <span className={labelClasses}>Enter</span>
        {enteredCount !== undefined ? (
          <span className={countClasses}>{enteredCount}</span>
        ) : null}
      </button>
      <button
        type="button"
        aria-pressed={exitActive}
        onClick={() => onChange("EXIT")}
        className={`${baseClasses} ${
          exitActive
            ? "bg-red-600 text-white focus:ring-red-300"
            : "bg-slate-800 text-red-400 border-2 border-red-700 focus:ring-red-700"
        }`}
      >
        <span className={labelClasses}>Exit</span>
        {exitedCount !== undefined ? (
          <span className={countClasses}>{exitedCount}</span>
        ) : null}
      </button>
    </div>
  );
}
